<template>
    <div class="_courseSlug">
        <LoadingSection :loading="course === null" :error="error"></LoadingSection>
        <Sections v-if="course">
            <SectionJumbo
                :heading="course.course_title"
                :content="course.course_excerpt"
                padding="py-24 md:py-32"
                :videoOpacity="videoOpacity"
                videoPosition="object-top"
                headingColor="text-white"
                contentColor="text-white"
                :image="course.course_jumbo_image_url"
                imageAlt="main image from this course by Andy Guitar"
                imageOpacity="opacity-30"
                :textCentered="false"
                :buttonRoute="jumboButtonRoute"
                :buttonText="jumboButtonText"
            ></SectionJumbo>
            <SectionVideo
                v-if="videoSources"
                heading="About this course"
                headingComponent="h2"
                :content="course.course_description_embed"
                :videoSources="videoSources"
                :videoPoster="videoPosterURL"
                videoAspectRatio="aspect-video"
                :flip="true"
            ></SectionVideo>
            <SectionText
                v-if="course.course_description_embed && !videoSources"
                heading="About this course"
                headingComponent="h2"
                :content="course.course_description_embed"
            ></SectionText>
            <SectionText
                v-if="lessons && lessons.length"
                heading="Lessons in this course"
                headingComponent="h2"
                class="bg-zinc-50"
            >
                <template v-slot:custom>
                    <ul class="mt-5">
                        <li v-for="lesson in lessons" :key="lesson.id_course_lesson">
                            <LayoutCourseLessonTile
                                v-if="lesson"
                                class="my-3 max-w-2xl"
                                :lesson="lesson"
                                :isPremiumMember="isPremiumMember"
                            ></LayoutCourseLessonTile>
                        </li>
                    </ul>
                </template>
            </SectionText>
            <LayoutScrollIntoView hash="#course-songs" behavior="smooth"></LayoutScrollIntoView>
            <SectionText
                v-if="!isPremiumMember && course?.course_songs?.length"
                heading="Recommended Songs"
                :flip="true"
                class="bg-zinc-100"
                content="<p>Enjoying this course? Want to test out your new skills? Find out my recommended song tutorials that accompany this course and get total access to the site by signing up today!</p>"
                :buttonIcon="currentUser?.eligible_for_aaa_free_trial ? 'IconTag' : null"
                :buttonText="currentUser?.eligible_for_aaa_free_trial ? 'Free Trial' : 'Membership'"
                :buttonRoute="useContextConversion().routeMembership('course-recommended-songs')"
            ></SectionText>
            <SectionCardsSwiper
                v-if="course?.course_songs?.length && isPremiumMember"
                id="section-Course-Songs"
                heading="Recommended Songs"
                headingComponent="h2"
                :cards="course.course_songs.map(song => ({
                    route: useContextLesson().route(song, 'course-song'),
                    image: 'https://d1ma7tzlswibx6.cloudfront.net/images-lesson/' + song.lesson_image,
                    imageAlt: 'The main image from a song lesson relevant to this course',
                    imageAspectRatio: 'aspect-[40/21] object-cover object-center',
                    heading: song.lesson_title,
                    imageOverlayLocked: song.lesson_locked
                }))"
            ></SectionCardsSwiper>
            <SectionTestimonials
                :testimonials="course.course_testimonials.map(t => ({
                    testimonialImage: t.course_testimonial_image,
                    testimonialName: t.course_testimonial_name,
                    testimonialContent: t.course_testimonial_content
                }))"
                heading="What our students say"
                headingComponent="h2"
                color="bg-zinc-100"
            ></SectionTestimonials>
        </Sections>
    </div>
</template>

<script setup>
const {course, error, firstLesson, resumeLesson} = storeToRefs(useStoreCourse());
const {currentUser, isLoggedIn} = storeToRefs(useStoreAuth());
const videoPosterURL = ref(null);
const courseSlug = useRoute().params.courseSlug;

await useLazyAsyncData('load', async () => {
    const promises = [];
    promises.push(
        useStoreCourse().getCourse(courseSlug)
    );
    if (isLoggedIn.value) promises.push(
        useStoreContinue().getContinue(),
        useStoreViewed().getViews(),
    );

    await Promise.all(promises);
    return true;
});

const videoOpacity = computed(() => {
    return 'opacity-40';
});
const videoSources = computed(() => {
    const {$videoUtilities} = useNuxtApp();
    if (!course.value) return null;
    if (!course.value.course_trailer_url) return null;
    return $videoUtilities.getCloudfrontVideoSources(course.value.course_trailer_url);
});
const jumboButtonRoute = computed(() => {
    if (resumeLesson.value?.lesson_route) return useContextLesson().route(resumeLesson.value, 'course', courseSlug);
    if (firstLesson.value?.lesson_route) return useContextLesson().route(firstLesson.value, 'course', courseSlug);
    return null;
});
const jumboButtonText = computed(() => {
    if (resumeLesson.value?.lesson_route) return 'Resume';
    if (firstLesson.value?.lesson_route) return 'Get Started';
    return null;
});
const lessons = computed(() => {
    if (!course?.value?.course_lessons?.length) return null;
    return course.value.course_lessons
        .map(l => {
            const lesson = {...l};
            lesson.lesson_route = useContextLesson().route(l, 'course', courseSlug);
            return lesson;
        });
});
const isPremiumMember = computed(() => {
    return !!currentUser.value?.has_aaa_membership;
});

async function getVideoPoster () {
    if (import.meta.server) return;
    if (!course.value?.course_trailer_url) return;
    const {posterURL, error} = await useEntityUpload().getVideoPoster(course.value?.course_trailer_url);
    videoPosterURL.value = posterURL;
    if (error) console.error('Error getting video poster from the API: ' + error);
}

watch(course, getVideoPoster, {immediate: true});

useSeoCourse().buildReactiveHead(course, error);
</script>
